import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  ButtonBase,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ScaleIcon from '@mui/icons-material/Scale';
import CalculateIcon from '@mui/icons-material/Calculate';
import DescriptionIcon from '@mui/icons-material/Description';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

import ManagerEditModeToggle from '../shared/ManagerEditModeToggle';
import EditTransactionSheet from '../shared/EditTransactionSheet';
import ConfirmDeleteDialog from '../shared/ConfirmDeleteDialog';
import ManagerModificationsList from '../shared/ManagerModificationsList';
import useTicketPhotoViewer from '../shared/useTicketPhotoViewer';
import { TRANSACTION } from '../../global/Server/endpoints';
import { TOKENS, fmt, shortOverColor } from '../theme';
import {
  badgeProps,
  isModified,
  enrichTxnDescription,
} from '../shared/auditFormat';

// Cash-left rows (sender "CASH LEFT TO X" and receiver "CASH LEFT FROM X")
// are produced by the rollover/accept flow and must not be hand-edited —
// editing them would desync the paired transaction on the other shift.
function isCashLeftPaired(row) {
  const d = row?.description?.toUpperCase() || '';
  return d.startsWith('CASH LEFT');
}
// The sender's "CASH LEFT TO X" row is hidden everywhere — that money has
// left this shift's bank. The receiver's "CASH LEFT FROM X" row stays in
// the bank because that cash is now part of this shift's bank intake.
function isCashLeftTo(row) {
  const d = row?.description?.toUpperCase() || '';
  return d.startsWith('CASH LEFT TO');
}

// Spec §3 — central operational document. Top-to-bottom layout:
// 1. Manager Edit Mode toggle
// 2. 4 KPI cards
// 3. Bank (+) table
// 4. Expense (−) table
// 5. Total Expenses Transactions (4 icon cards, tappable in edit mode)
// 6. Manage Other Reports (edit mode only)
// 7. Reconciliation
// 8. Short/Over hero
// 9. Manager Modifications
export default function ShiftDetail({
  session,
  sessionData,
  customers,
  employees,
  categories,
  manageBonusList,
  onEditTransaction,
  onDeleteTransaction,
  onAddTransaction,
  onEditCashLeft,
  onNavigateReport,
  // When true the component renders as a pure review surface: the Manager
  // Edit Mode toggle is suppressed and edit mode can never be entered. Used
  // by the Weekly Report audit log, which only views shifts (never mutates).
  readOnly = false,
}) {
  const [editModeRaw, setEditMode] = useState(false);
  const editMode = readOnly ? false : editModeRaw;
  const [editSheet, setEditSheet] = useState(null); // { kind, mode, entry }
  const [confirmDelete, setConfirmDelete] = useState(null); // { kind, entry }
  const [cashEdit, setCashEdit] = useState(null); // string while dialog open
  const [commentDialog, setCommentDialog] = useState(null); // row or null

  // Full-screen viewer for a transaction's "EXTRA MATCH / LUCKY" photo,
  // fetched on demand from /transaction/previewPhoto by transactionId.
  const txnPhotoViewer = useTicketPhotoViewer({
    endpoint: TRANSACTION.PREVIEW_PHOTO,
    idKey: 'transactionId',
    title: 'Transaction Photo',
  });

  // "CASH LEFT TO X" rows represent cash handed off to another shift and
  // must not show here — that money is no longer in this bank. "CASH LEFT
  // FROM X" rows represent cash received from the prior shift and DO belong
  // in the bank (real intake), so they remain.
  const bank = (sessionData?.bank || []).filter(
    (r) => r.entryStatus !== 'deleted' && !isCashLeftTo(r)
  );
  const expenses = (sessionData?.expenses || []).filter(
    (r) => r.entryStatus !== 'deleted'
  );
  const bonuses = (sessionData?.bonuses || []).filter(
    (b) => b.status !== 'deleted'
  );
  const points = sessionData?.points || [];
  const raffles = (sessionData?.raffles || []).filter(
    (r) => r.status !== 'deleted'
  );
  const ticketOuts = (sessionData?.ticketOuts || []).filter(
    (t) => t.status !== 'deleted'
  );
  const auditLog = sessionData?.auditLog || [];

  const totalBank = useMemo(
    () => bank.reduce((s, r) => s + Number(r.amount || 0), 0),
    [bank]
  );
  const totalExpensesOnly = useMemo(
    () => expenses.reduce((s, r) => s + Number(r.amount || 0), 0),
    [expenses]
  );
  const totalBonuses = useMemo(
    () => bonuses.reduce((s, b) => s + Number(b.bonusAmount || 0), 0),
    [bonuses]
  );
  const totalPoints = useMemo(
    () => points.reduce((s, p) => s + Number(p.points || 0), 0),
    [points]
  );
  const totalTicketOuts = useMemo(
    () => ticketOuts.reduce((s, t) => s + Number(t.ticketOut || 0), 0),
    [ticketOuts]
  );
  const totalRaffles = useMemo(
    () => raffles.reduce((s, r) => s + Number(r.amount || 0), 0),
    [raffles]
  );
  const totalOutgoing =
    totalExpensesOnly +
    totalBonuses +
    totalPoints +
    totalTicketOuts +
    totalRaffles;
  const closingCash = Number(sessionData?.cashLeft || 0);
  const difference = totalBank - totalOutgoing;
  const shortOver = closingCash - difference;

  const handleEdit = (kind, entry) =>
    setEditSheet({ kind, mode: 'edit', entry });
  const handleAdd = (kind) => setEditSheet({ kind, mode: 'add', entry: {} });
  const handleSaveEdit = (payload) => {
    if (editSheet.mode === 'add') {
      const type = editSheet.kind === 'bank' ? 'bank' : 'expense';
      onAddTransaction({ ...payload, type });
    } else {
      onEditTransaction(editSheet.entry, payload);
    }
    setEditSheet(null);
  };
  const handleConfirmDelete = () => {
    if (!confirmDelete) return;
    onDeleteTransaction(confirmDelete.entry);
    setConfirmDelete(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1.5 }}>
      {!readOnly && (
        <ManagerEditModeToggle
          on={editMode}
          onToggle={() => setEditMode((m) => !m)}
        />
      )}

      {/* 4 KPIs */}
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <KpiCard
            icon={<AccountBalanceIcon />}
            label="Total Bank"
            value={fmt(totalBank)}
            accent={TOKENS.emerald700}
            borderColor={TOKENS.emerald200}
            bg={TOKENS.emerald50}
          />
        </Grid>
        <Grid item xs={6}>
          <KpiCard
            icon={<TrendingDownIcon />}
            label="Total Expenses"
            value={fmt(totalOutgoing)}
            accent={TOKENS.red700}
            borderColor={TOKENS.red200}
            bg={TOKENS.red50}
          />
        </Grid>
        <Grid item xs={6}>
          <KpiCard
            icon={<ScaleIcon />}
            label="Short / Over"
            value={`${shortOver > 0 ? '+' : ''}${fmt(shortOver)}`}
            accent={shortOverColor(shortOver)}
            borderColor={shortOver < 0 ? TOKENS.red200 : TOKENS.emerald200}
            bg={shortOver < 0 ? TOKENS.red50 : TOKENS.emerald50}
          />
        </Grid>
        <Grid item xs={6}>
          <KpiCard
            icon={<CalculateIcon />}
            label="Closing Cash"
            value={fmt(closingCash)}
            accent={TOKENS.blue700}
            borderColor={TOKENS.blue200}
            bg={TOKENS.blue50}
          />
        </Grid>
      </Grid>

      {/* Bank table */}
      <TxnTable
        rows={bank}
        amountKey="amount"
        title="Bank (+)"
        countLabel="entries"
        themeColor={TOKENS.emerald700}
        themeBg={TOKENS.emerald50}
        themeBorder={TOKENS.emerald300}
        editMode={editMode}
        onEdit={(row) => handleEdit('bank', row)}
        onDelete={(row) => setConfirmDelete({ kind: 'bank', entry: row })}
        onAdd={() => handleAdd('bank')}
        addLabel="Add Bank Entry"
        total={totalBank}
        customers={customers}
        employees={employees}
        onShowComment={setCommentDialog}
        onShowPhoto={(row) => txnPhotoViewer.view(row.transactionId)}
      />

      {/* Expense table */}
      <TxnTable
        rows={expenses}
        amountKey="amount"
        title="Expenses (−)"
        countLabel="entries"
        themeColor={TOKENS.red700}
        themeBg={TOKENS.red50}
        themeBorder={TOKENS.red300}
        editMode={editMode}
        onEdit={(row) => handleEdit('expense', row)}
        onDelete={(row) => setConfirmDelete({ kind: 'expense', entry: row })}
        onAdd={() => handleAdd('expense')}
        addLabel="Add Expense"
        total={totalExpensesOnly}
        customers={customers}
        employees={employees}
        onShowComment={setCommentDialog}
        onShowPhoto={(row) => txnPhotoViewer.view(row.transactionId)}
      />

      {/* Total Expenses Transactions — sub-report icon cards */}
      <SubReportSummary
        editMode={editMode}
        onNavigateReport={onNavigateReport}
        points={{ count: points.length, value: totalPoints }}
        bonuses={{ count: bonuses.length, value: totalBonuses }}
        raffles={{ count: raffles.length, value: totalRaffles }}
        expenses={{ count: expenses.length, value: totalExpensesOnly }}
        ticketOut={{ count: ticketOuts.length, value: totalTicketOuts }}
        total={totalOutgoing}
      />

      {/* Manage Other Reports (Manager Mode only) */}
      {editMode && onNavigateReport && (
        <ManageOtherReports onNavigate={onNavigateReport} />
      )}

      {/* Reconciliation */}
      <Reconciliation
        totalBank={totalBank}
        totalOutgoing={totalOutgoing}
        closingCash={closingCash}
        difference={difference}
        shortOver={shortOver}
        cashLeftEmployeeName={sessionData?.cashLeftEmployeeName}
        editMode={editMode}
        onEditCash={
          onEditCashLeft ? () => setCashEdit(String(closingCash)) : null
        }
      />

      {/* Manager Modifications */}
      <ManagerModificationsList
        auditLog={auditLog}
        customers={customers}
        employees={employees}
        manageBonusList={manageBonusList}
      />

      {/* Edit sheet + confirm */}
      {editSheet && (
        <EditTransactionSheet
          // Force remount per open so state doesn't leak across.
          key={`${editSheet.kind}-${editSheet.mode}-${
            editSheet.entry?.transactionId || 'new'
          }`}
          open
          onClose={() => setEditSheet(null)}
          onSave={handleSaveEdit}
          kind={editSheet.kind}
          mode={editSheet.mode}
          entry={editSheet.entry}
          customers={customers}
          employees={employees}
          categories={categories}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteDialog
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={handleConfirmDelete}
          title="Delete this transaction?"
          subtitle={`${confirmDelete.entry?.description || ''} · $${fmt(
            confirmDelete.entry?.amount || 0
          )}`}
        />
      )}
      {cashEdit !== null && (
        <EditCashDialog
          initialValue={cashEdit}
          currentValue={closingCash}
          onClose={() => setCashEdit(null)}
          onSave={(val) => {
            onEditCashLeft(val);
            setCashEdit(null);
          }}
        />
      )}

      <Dialog
        open={commentDialog != null}
        onClose={() => setCommentDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, padding: 16 } }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Comments
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {commentDialog?.dateTime && (
            <Typography
              variant="body2"
              sx={{ color: TOKENS.slate500, fontWeight: 600, mb: 1 }}
            >
              {commentDialog.dateTime}
            </Typography>
          )}
          <Typography
            variant="body1"
            sx={{ color: '#444', whiteSpace: 'pre-line' }}
          >
            {commentDialog?.comments || ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCommentDialog(null)}
            variant="contained"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {txnPhotoViewer.node}
    </Box>
  );
}

function EditCashDialog({ initialValue, currentValue, onClose, onSave }) {
  const [value, setValue] = useState(initialValue);
  const parsed = parseFloat(value);
  const valid = !Number.isNaN(parsed) && parsed >= 0;
  const unchanged = valid && parsed === Number(currentValue);
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        Edit Closing Cash
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12, color: TOKENS.slate500, mb: 1.5 }}>
          Overriding this value is recorded in Manager Modifications.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          type="number"
          inputProps={{ step: '0.01', min: 0 }}
          label="Cash amount"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || unchanged}
          onClick={() => onSave(parsed)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function KpiCard({ icon, label, value, accent, borderColor, bg }) {
  return (
    <Box
      sx={{
        background: '#fff',
        border: `1px solid ${borderColor}`,
        borderRadius: 2,
        p: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { sx: { color: accent, fontSize: 16 } })}
        </Box>
        <Typography
          sx={{
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.slate500,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 700,
          fontFamily: 'monospace',
          color: accent,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function TxnTable({
  rows,
  amountKey,
  title,
  countLabel,
  themeColor,
  themeBg,
  themeBorder,
  editMode,
  onEdit,
  onDelete,
  onAdd,
  addLabel,
  total,
  customers,
  employees,
  onShowComment,
  onShowPhoto,
}) {
  return (
    <Box
      sx={{
        background: '#fff',
        border: `1px solid ${themeBorder}80`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          background: themeBg,
          borderBottom: `1px solid ${themeBorder}80`,
          px: 1.5,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: themeColor,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: 10, color: themeColor }}>
          {rows.length} {countLabel}
        </Typography>
      </Box>
      {rows.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 2,
            color: TOKENS.slate400,
            fontSize: 12,
          }}
        >
          No entries.
        </Box>
      )}
      {rows.map((row, i) => {
        const badge = badgeProps(row);
        return (
          <Box
            key={row.transactionId || i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              borderBottom:
                i < rows.length - 1 ? `1px solid ${TOKENS.slate100}` : 'none',
              background: isModified(row) ? themeBg + '80' : 'transparent',
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontFamily: 'monospace',
                color: TOKENS.slate400,
                width: 18,
                flexShrink: 0,
              }}
            >
              {i + 1}
            </Typography>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
              <Typography
                sx={{ fontSize: 12, fontWeight: 500, color: TOKENS.slate900 }}
                noWrap
              >
                {enrichTxnDescription(row, customers, employees)}
              </Typography>
              {row.comments && onShowComment && (
                <Tooltip title="View Breakdown">
                  <IconButton
                    size="small"
                    onClick={() => onShowComment(row)}
                    sx={{ p: 0.25 }}
                  >
                    <DescriptionIcon
                      sx={{ fontSize: 14, color: TOKENS.slate500 }}
                    />
                  </IconButton>
                </Tooltip>
              )}
              {row.photoPath && onShowPhoto && (
                <Tooltip title="View photo">
                  <IconButton
                    size="small"
                    onClick={() => onShowPhoto(row)}
                    sx={{ p: 0.25 }}
                  >
                    <PhotoCameraIcon
                      sx={{ fontSize: 14, color: TOKENS.slate500 }}
                    />
                  </IconButton>
                </Tooltip>
              )}
              {row.modified && (
                <StarIcon sx={{ color: themeColor, fontSize: 12 }} />
              )}
              {badge && (
                <Chip
                  size="small"
                  label={badge.label}
                  sx={{
                    height: 14,
                    fontSize: 8,
                    fontWeight: 700,
                    background: badge.bg,
                    color: badge.color,
                  }}
                />
              )}
              </Box>
              <Typography
                sx={{
                  fontSize: 10,
                  fontFamily: 'monospace',
                  color: TOKENS.slate500,
                  whiteSpace: 'nowrap',
                }}
              >
                {row.dateTime ? String(row.dateTime).toUpperCase() : '—'}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                fontFamily: 'monospace',
                color: themeColor,
                textAlign: 'right',
              }}
            >
              {fmt(row[amountKey] || 0)}
            </Typography>
            {editMode && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {!isCashLeftPaired(row) && (
                  <IconButton
                    size="small"
                    onClick={() => onEdit(row)}
                    sx={{
                      background: TOKENS.slate100,
                      '&:hover': { background: TOKENS.slate200 },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 14, color: TOKENS.slate700 }} />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={() => onDelete(row)}
                  sx={{
                    background: TOKENS.red50,
                    '&:hover': { background: TOKENS.red100 },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 14, color: TOKENS.red600 }} />
                </IconButton>
              </Box>
            )}
          </Box>
        );
      })}
      {editMode && (
        <ButtonBase
          onClick={onAdd}
          sx={{
            width: '100%',
            py: 1.25,
            borderTop: `1px solid ${themeBorder}40`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          <AddIcon sx={{ color: themeColor, fontSize: 16 }} />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: themeColor,
            }}
          >
            {addLabel}
          </Typography>
        </ButtonBase>
      )}
      <Box
        sx={{
          background: themeBg,
          borderTop: `1px solid ${themeBorder}80`,
          px: 1.5,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: themeColor,
          }}
        >
          Total
        </Typography>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            fontFamily: 'monospace',
            color: themeColor,
          }}
        >
          {fmt(total)}
        </Typography>
      </Box>
    </Box>
  );
}

function SubReportSummary({
  editMode,
  onNavigateReport,
  points,
  bonuses,
  raffles,
  expenses,
  ticketOut,
  total,
}) {
  const cards = [
    {
      id: 'points',
      label: 'Points',
      count: points.count,
      value: points.value,
      color: TOKENS.blue700,
      bg: TOKENS.blue50,
    },
    {
      id: 'bonus',
      label: 'Bonuses',
      count: bonuses.count,
      value: bonuses.value,
      color: TOKENS.amber700,
      bg: TOKENS.amber50,
    },
    {
      id: 'raffle',
      label: 'Raffles',
      count: raffles.count,
      value: raffles.value,
      color: TOKENS.pink700,
      bg: TOKENS.pink50,
    },
    {
      id: null,
      label: 'Expenses',
      count: expenses.count,
      value: expenses.value,
      color: TOKENS.slate700,
      bg: TOKENS.slate100,
    },
    {
      id: 'ticket',
      label: 'Ticket-Out',
      count: ticketOut.count,
      value: ticketOut.value,
      color: TOKENS.violet700,
      bg: TOKENS.violet50,
    },
  ];
  return (
    <Box
      sx={{
        background: '#fff',
        border: `1px solid ${TOKENS.slate200}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          background: TOKENS.slate50,
          borderBottom: `1px solid ${TOKENS.slate200}`,
          px: 1.5,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.slate700,
          }}
        >
          Total Expenses Transactions
        </Typography>
        {editMode && (
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              color: TOKENS.amber700,
              textTransform: 'uppercase',
            }}
          >
            Tap to manage
          </Typography>
        )}
      </Box>
      <Grid container spacing={1} sx={{ p: 1.5 }}>
        {cards.map((c, i) => (
          <Grid item xs={6} key={i}>
            <ButtonBase
              disabled={!editMode || !c.id}
              onClick={() => onNavigateReport && c.id && onNavigateReport(c.id)}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 0.5,
                textAlign: 'left',
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: c.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <StarIcon sx={{ color: c.color, fontSize: 16 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 9, color: TOKENS.slate500 }}>
                  {c.label} ({c.count})
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                  }}
                >
                  {fmt(c.value)}
                </Typography>
              </Box>
              {editMode && c.id && (
                <ChevronRightIcon
                  sx={{ color: TOKENS.slate400, fontSize: 14 }}
                />
              )}
            </ButtonBase>
          </Grid>
        ))}
      </Grid>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderTop: `1px solid ${TOKENS.slate200}`,
          background: TOKENS.slate50,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
          Total Expenses
        </Typography>
        <Typography
          sx={{ fontSize: 15, fontWeight: 700, fontFamily: 'monospace' }}
        >
          {fmt(total)}
        </Typography>
      </Box>
    </Box>
  );
}

function ManageOtherReports({ onNavigate }) {
  const items = [
    {
      id: 'bonus',
      label: 'Bonus Report',
      desc: 'Audit, approve, flag bonuses',
    },
    {
      id: 'raffle',
      label: 'Raffle Report',
      desc: 'Verify identity, approve winners',
    },
    { id: 'points', label: 'Points Report', desc: 'Review matches given' },
    {
      id: 'ticket',
      label: 'Ticket Out Report',
      desc: 'Review all ticket payouts',
    },
  ];
  return (
    <Box
      sx={{
        background: '#fff',
        border: `1px solid ${TOKENS.amber200}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          background: TOKENS.amber50,
          borderBottom: `1px solid ${TOKENS.amber200}`,
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <EditIcon sx={{ color: TOKENS.amber700, fontSize: 14 }} />
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.amber900,
          }}
        >
          Manage Other Reports
        </Typography>
      </Box>
      {items.map((it, i) => (
        <ButtonBase
          key={it.id}
          onClick={() => onNavigate(it.id)}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 1,
            textAlign: 'left',
            borderBottom:
              i < items.length - 1 ? `1px solid ${TOKENS.slate100}` : 'none',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.slate900 }}
            >
              {it.label}
            </Typography>
            <Typography sx={{ fontSize: 11, color: TOKENS.slate500 }}>
              {it.desc}
            </Typography>
          </Box>
          <ChevronRightIcon sx={{ color: TOKENS.slate400 }} />
        </ButtonBase>
      ))}
    </Box>
  );
}

function Reconciliation({
  totalBank,
  totalOutgoing,
  closingCash,
  difference,
  shortOver,
  cashLeftEmployeeName,
  editMode,
  onEditCash,
}) {
  // When cash was handed to another employee instead of closing the shift,
  // the backend returns that recipient's name — surface it inline so the
  // label reflects what actually happened (matches the legacy PDF).
  const cashLabel = cashLeftEmployeeName
    ? `Cash (${cashLeftEmployeeName})`
    : 'Cash (Closing Shift)';
  return (
    <Box
      sx={{
        background: '#fff',
        border: `1px solid ${TOKENS.slate200}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          background: TOKENS.slate50,
          borderBottom: `1px solid ${TOKENS.slate200}`,
          px: 1.5,
          py: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.slate700,
          }}
        >
          Reconciliation
        </Typography>
      </Box>
      <Row label="Total Bank" value={fmt(totalBank)} />
      <Row label="Total Expenses" value={fmt(totalOutgoing)} />
      <Row
        label="Difference"
        value={fmt(difference)}
        bold
        subtle="(Bank − Expenses)"
      />
      <Row
        label={cashLabel}
        value={fmt(closingCash)}
        action={
          editMode && onEditCash ? (
            <IconButton
              size="small"
              onClick={onEditCash}
              sx={{
                ml: 0.5,
                background: TOKENS.slate100,
                '&:hover': { background: TOKENS.slate200 },
              }}
            >
              <EditIcon sx={{ fontSize: 14, color: TOKENS.slate700 }} />
            </IconButton>
          ) : null
        }
      />
      <Box
        sx={{
          px: 1.5,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          background: shortOver < 0 ? TOKENS.red50 : TOKENS.emerald50,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: shortOver < 0 ? TOKENS.red700 : TOKENS.emerald700,
          }}
        >
          Short / Over
        </Typography>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            fontFamily: 'monospace',
            color: shortOver < 0 ? TOKENS.red700 : TOKENS.emerald700,
          }}
        >
          {shortOver > 0 ? '+' : ''}
          {fmt(shortOver)}
        </Typography>
      </Box>
    </Box>
  );
}

function Row({ label, value, bold, subtle, action }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: bold ? TOKENS.slate50 : 'transparent',
        borderBottom: `1px solid ${TOKENS.slate100}`,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: TOKENS.slate700,
          fontWeight: bold ? 700 : 400,
        }}
      >
        {label}
        {subtle && (
          <Typography
            component="span"
            sx={{
              fontSize: 9,
              color: TOKENS.slate400,
              ml: 0.5,
            }}
          >
            {subtle}
          </Typography>
        )}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: 'monospace',
            fontWeight: bold ? 700 : 600,
            color: TOKENS.slate900,
          }}
        >
          {value}
        </Typography>
        {action}
      </Box>
    </Box>
  );
}
